import { useEffect, useState } from 'react';

import { LinkItem } from '@occc-app/design-system';
import {
  Award_Per_Counting_Strategy,
  EarningMechanismConfigurationInput,
  EarningMechanismCreateInput,
  EarningMechanismFragment,
  EarningMechanismUpdateInput,
} from '@occc/graphql';
import { routeCreator as programManagementRouteCreator } from '@occc-app/routes/ProgramManagement/utils';
import { dollarsToCents } from '@occc-app/lib/currency';
import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { earningMechanismInputCreator } from '@occc-app/offerHelpers/earningMechanismInputCreator';
import { getAwardPointsOrVouchersOrNullInput } from '@occc-app/offerHelpers/utils/getAwardInput';

import {
  CONFIGURE_PAGE_STATE,
  ConfigureFormValues,
  PRODUCT_DISCOUNT_TYPE,
  ProductDiscountFormValues,
  AWARD_TYPES,
  TRANSACTION_DISCOUNT_TYPE,
  TransactionDiscountFormValues,
  FuelRollbackFormValues,
  ExternalFulfillmentFormValues,
  TransactionDiscountValues,
  ProductDiscountValues,
  ExternalFulfillmentValues,
  FuelRollbackValues,
} from './types';
import { messages } from './messages';

const AWARD_CATALOG_BASE_URL = '/award-catalog';

export const routeCreator = {
  base: () => AWARD_CATALOG_BASE_URL,
  create: () => `${AWARD_CATALOG_BASE_URL}/create`,
  details: (id: string) => `${AWARD_CATALOG_BASE_URL}/${id}`,
  update: (id: string) => `${AWARD_CATALOG_BASE_URL}/${id}/update`,
};

export const getAwardCreatePageHeaderLinks = (): LinkItem[] => [
  {
    to: programManagementRouteCreator.base(),
    label: 'Program Management',
  },
  {
    to: routeCreator.base(),
    label: messages.title,
  },
  {
    key: routeCreator.create(),
    label: 'Create Award Catalog',
  },
];

export const getAwardListPageHeaderLinks = (): LinkItem[] => [
  {
    to: programManagementRouteCreator.base(),
    label: 'Program Management',
  },
  {
    key: routeCreator.base(),
    label: 'Award Catalog',
  },
];

export const getAwardCatalogUpdatePageHeaderLinks = (
  name: string,
  id: string,
  page: string,
): LinkItem[] => [
  {
    to: programManagementRouteCreator.base(),
    label: 'Program Management',
  },
  {
    to: routeCreator.base(),
    label: messages.title,
  },
  {
    to: routeCreator.details(id),
    label: `${name}`,
  },
  {
    key: routeCreator.update(id),
    label: `${page}`,
  },
];

// This is for taking a string ex. 5.5 and turning it into a cent amount, ex. 550
const formatMoney = (amount: string) => {
  const amountValues = amount.split('.');

  let cents = amountValues[1];

  if (!cents) {
    cents = '00';
  } else if (cents?.length === 1) {
    cents = cents + '0';
  }

  return parseInt(amountValues[0] + cents);
};

const mapFormValuesToTransactionDiscountInput = (
  values: TransactionDiscountFormValues,
): EarningMechanismConfigurationInput => {
  const { type, amount, productGroups, purchaseAmountThreshold } = values;

  const ineligibleProductGroupIds = !productGroups
    ? []
    : productGroups.map((product) => {
        return product.id;
      });

  const input: EarningMechanismConfigurationInput = {};

  if (type === TRANSACTION_DISCOUNT_TYPE.DOLLARS) {
    const moneyDiscountCents = dollarsToCents(amount ?? 0);
    const purchaseAmountThresholdCents = dollarsToCents(purchaseAmountThreshold ?? 0);

    input.transactionDiscount = {
      awardTransactionMoneyDiscount: {
        moneyDiscount: moneyDiscountCents,
      },
      purchaseAmountThreshold: purchaseAmountThresholdCents,
      ineligibleProductGroupIds,
    };
  }

  if (type === TRANSACTION_DISCOUNT_TYPE.PERCENT) {
    const purchaseAmountThresholdCents = dollarsToCents(purchaseAmountThreshold ?? 0);

    input.transactionDiscount = {
      awardTransactionPercentDiscount: {
        percentDiscount: amount,
      },
      purchaseAmountThreshold: purchaseAmountThresholdCents,
      ineligibleProductGroupIds,
    };
  }

  return input;
};

const mapFormValuesToProductDiscountInput = (
  values: ProductDiscountFormValues,
): EarningMechanismConfigurationInput => {
  const { type, amount, productGroups, partialDiscount, allowCarryOver } = values;

  const eligibleProductGroupIds = !productGroups
    ? []
    : productGroups.map((product) => {
        return product.id;
      });

  const input: EarningMechanismConfigurationInput = {};

  if (type === PRODUCT_DISCOUNT_TYPE.DISCOUNTED) {
    const parsedAmount = formatMoney(amount);

    input.productDiscount = {
      awardMoneyDiscount: {
        moneyAmount: parsedAmount,
        awardPer: values.discountType,
        partialDiscount:
          values.discountType === Award_Per_Counting_Strategy.TotalPurchase
            ? partialDiscount
            : false,
      },
      purchaseThreshold: 1,
      eligibleProductGroupIds,
      allowCarryOver: allowCarryOver ?? false,
    };
  }

  if (type === PRODUCT_DISCOUNT_TYPE.FREE) {
    input.productDiscount = {
      awardPriceOverride: {
        maximumCost: 0,
      },
      purchaseThreshold: 1,
      eligibleProductGroupIds,
    };
  }

  if (type === PRODUCT_DISCOUNT_TYPE.OVERRIDE) {
    const parsedAmount = formatMoney(amount);

    input.productDiscount = {
      awardPriceOverride: {
        maximumCost: parsedAmount,
      },
      purchaseThreshold: 1,
      eligibleProductGroupIds,
    };
  }

  return input;
};

const mapFuelRollbackFormValuesToInput = (
  values: FuelRollbackFormValues,
): EarningMechanismConfigurationInput => {
  const { amount, productGroups } = values;

  const eligibleProductGroupIds = productGroups.map((product) => {
    return product.id;
  });

  return {
    productDiscount: {
      purchaseThreshold: 1,
      eligibleProductGroupIds,
      awardMoneyDiscount: {
        moneyAmount: formatMoney(String(amount)),
        awardPer: Award_Per_Counting_Strategy.IndividualPurchase,
        partialDiscount: false,
      },
    },
  };
};

const mapExternalFulfillmentFormValuesToInput = (
  values: ExternalFulfillmentFormValues,
): EarningMechanismConfigurationInput => {
  const { awardPoints, awardVouchers, awardType } = values;

  const awardInput = getAwardPointsOrVouchersOrNullInput({
    awardType,
    awardPoints,
    awardVouchers,
  });

  return earningMechanismInputCreator.createExternalFulfillment(awardInput);
};

// Will return null when called with SWEEPSTAKES
const inputMap = new Map<
  AWARD_TYPES,
  (values: ConfigureFormValues) => EarningMechanismConfigurationInput | null
>([
  [
    AWARD_TYPES.TRANSACTION,
    (values: TransactionDiscountValues) => {
      if (values.transactionDiscount) {
        return mapFormValuesToTransactionDiscountInput(values.transactionDiscount);
      } else {
        return null;
      }
    },
  ],
  [
    AWARD_TYPES.PRODUCT,
    (values: ProductDiscountValues) => {
      if (values.productDiscount) {
        return mapFormValuesToProductDiscountInput(values.productDiscount);
      } else {
        return null;
      }
    },
  ],
  [
    AWARD_TYPES.SWEEPSTAKES,
    () => {
      return null;
    },
  ],
  [
    AWARD_TYPES.FUEL_ROLLBACK,
    (values: FuelRollbackValues) => {
      if (values.fuelRollback) {
        return mapFuelRollbackFormValuesToInput(values.fuelRollback);
      }

      return null;
    },
  ],
  [
    AWARD_TYPES.EXTERNAL_FULFILLMENT,
    (values: ExternalFulfillmentValues) => {
      if (values.externalFulfillment) {
        return mapExternalFulfillmentFormValuesToInput(values.externalFulfillment);
      }

      return null;
    },
  ],
]);

type ConfigurationInput = Omit<EarningMechanismCreateInput, 'configuration'> & {
  configuration: EarningMechanismConfigurationInput | null;
};

export const createEarningMechanismCreateInput = (
  offerId: string,
  values: ConfigureFormValues,
): ConfigurationInput | null => {
  if (!values.awardType) {
    return null;
  }

  const inputCreator = inputMap.get(values.awardType);

  if (!inputCreator) {
    return null;
  }

  return {
    offerId,
    configuration: inputCreator(values),
  };
};

type UpdateConfigurationInput = Omit<EarningMechanismUpdateInput, 'configuration'> & {
  configuration: EarningMechanismConfigurationInput | null;
};

export const createEarningMechanismUpdateInput = (
  earningMechanismId: string,
  values: ConfigureFormValues,
): UpdateConfigurationInput | null => {
  if (!values.awardType) {
    return null;
  }

  const inputCreator = inputMap.get(values.awardType);

  if (!inputCreator) {
    return null;
  }

  return {
    id: earningMechanismId,
    configuration: inputCreator(values),
  };
};

export const getPages = (awardType: AWARD_TYPES) => {
  const isTargetPageEnabled = getIsTargetTabEnabled(awardType);
  return [
    CONFIGURE_PAGE_STATE.CONFIGURE,
    ...(isTargetPageEnabled ? [CONFIGURE_PAGE_STATE.TARGET] : []),
    CONFIGURE_PAGE_STATE.DESIGN,
    CONFIGURE_PAGE_STATE.FINALIZE,
  ];
};

export const useGetAwardType = (earningMechanism?: EarningMechanismFragment | null) => {
  const [awardType, setAwardType] = useState<AWARD_TYPES>(AWARD_TYPES.SWEEPSTAKES);

  useEffect(() => {
    if (earningMechanism) {
      if (earningMechanismHelper.isTransactionDiscount(earningMechanism)) {
        setAwardType(AWARD_TYPES.TRANSACTION);
      }

      if (earningMechanismHelper.isProductDiscount(earningMechanism)) {
        setAwardType(AWARD_TYPES.PRODUCT);
      }
    }
  }, [earningMechanism]);

  return { awardType, setAwardType };
};

export const getIsTargetTabEnabled = (awardType?: AWARD_TYPES) =>
  awardType !== AWARD_TYPES.SWEEPSTAKES && awardType !== AWARD_TYPES.EXTERNAL_FULFILLMENT;
